// Country → region mapping for /browse.
//
// M5.4 follow-up to handover queue item #4. The bulk discovery import
// landed 86 distinct values in `players.nationality`, which is too
// many cards on /browse to scan at a glance. Aggregating to 4 broad
// regions (Americas, Europe, Asia-Pacific, Africa) plus an "Other"
// bucket gives coaches a top-down entry that fits one screen.
//
// Editorial calls baked into the map:
//   - Israel + Turkey → Europe. Both are basketball-ecosystem-aligned
//     (EuroBasket + EuroLeague) regardless of how a strict
//     geographic-region scheme would label them. Coaches looking for
//     prospects from those countries will look under Europe.
//   - Lebanon / Palestine / Jordan → Asia-Pacific. Only ~4 players
//     total; merging into Asia-Pacific avoids a 3-country "Middle East"
//     bucket that adds clutter without surfacing meaningful volume.
//   - UK + England both present as separate DB entries (the bulk import
//     mirrored API-Sports' inconsistent labeling); both → Europe.
//
// "Other" is for stragglers that aren't real countries — the bulk
// import inadvertently surfaced team names from API-Sports' `country`
// field for ~13 entries (Real Madrid, Fenerbahce, Partizan, etc.).
// They stay browseable via /browse/country/:slug but don't pollute the
// regional cards. The lookup helper returns "Other" for anything not
// in the map so new countries flowing in (e.g. from a wider import)
// land somewhere by default.
//
// Pure data — no React, no fetch. Used by Browse.tsx for client-side
// aggregation; deliberately not exposed as an API endpoint to avoid a
// new cache key.

export type RegionSlug = 'americas' | 'europe' | 'asia-pacific' | 'africa' | 'other'

export interface RegionMeta {
  slug: RegionSlug
  name: string
  // Optional copy shown on the region's page hero. Empty string OK.
  blurb: string
}

// Ordered for the BrowseIndex render. "Other" is intentionally last so
// it doesn't draw attention away from the real regions.
export const REGIONS: readonly RegionMeta[] = [
  {
    slug: 'americas',
    name: 'Americas',
    blurb: 'Players from North, Central, and South America.',
  },
  {
    slug: 'europe',
    name: 'Europe',
    blurb:
      'Players from across Europe, including Turkey and Israel — both basketball-ecosystem-aligned (EuroBasket + EuroLeague).',
  },
  {
    slug: 'asia-pacific',
    name: 'Asia-Pacific',
    blurb:
      'Players from East and Southeast Asia, Oceania, and the Levant (Lebanon, Palestine, Jordan grouped here for v1).',
  },
  {
    slug: 'africa',
    name: 'Africa',
    blurb: 'Players from across the African continent.',
  },
  {
    slug: 'other',
    name: 'Other',
    blurb:
      'Entries that landed in the corpus without a clean country mapping — mostly team names that came through the bulk import. Still browseable, just not regionally aggregated.',
  },
]

// Lookup by name as it appears in players.nationality (i.e. whatever
// the import / agent typed in). Case-sensitive against the canonical
// strings; the helper below collapses case at the boundary so callers
// don't need to.
//
// To extend: add the canonical-cased name to the appropriate region.
// Unmapped countries default to 'other' via getRegionFor() below.
const COUNTRY_TO_REGION: Record<string, RegionSlug> = {
  // --- Americas ----------------------------------------------------
  USA: 'americas',
  Canada: 'americas',
  Argentina: 'americas',
  Brazil: 'americas',
  Chile: 'americas',
  Colombia: 'americas',
  Cuba: 'americas',
  'Dominican Republic': 'americas',
  Jamaica: 'americas',
  Panama: 'americas',
  'Puerto Rico': 'americas',
  'United States Virgin Islands': 'americas',

  // --- Europe ------------------------------------------------------
  Serbia: 'europe',
  Croatia: 'europe',
  France: 'europe',
  Belgium: 'europe',
  Slovenia: 'europe',
  'Bosnia and Herzegovina': 'europe',
  Lithuania: 'europe',
  Italy: 'europe',
  Spain: 'europe',
  Greece: 'europe',
  Israel: 'europe',
  Turkey: 'europe',
  Montenegro: 'europe',
  'North Macedonia': 'europe',
  Germany: 'europe',
  Latvia: 'europe',
  'Czech Republic': 'europe',
  Slovakia: 'europe',
  Hungary: 'europe',
  Estonia: 'europe',
  Finland: 'europe',
  Sweden: 'europe',
  Denmark: 'europe',
  Netherlands: 'europe',
  Luxembourg: 'europe',
  Iceland: 'europe',
  Poland: 'europe',
  Romania: 'europe',
  Bulgaria: 'europe',
  Switzerland: 'europe',
  Ukraine: 'europe',
  Russia: 'europe',
  Belarus: 'europe',
  Cyprus: 'europe',
  Austria: 'europe',
  Armenia: 'europe',
  Azerbaijan: 'europe',
  Georgia: 'europe',
  England: 'europe',
  'United Kingdom': 'europe',

  // --- Asia-Pacific (includes Levant for v1 — see header comment) -
  'South Korea': 'asia-pacific',
  Philippines: 'asia-pacific',
  Australia: 'asia-pacific',
  'New Zealand': 'asia-pacific',
  Taiwan: 'asia-pacific',
  Vietnam: 'asia-pacific',
  Tonga: 'asia-pacific',
  Guam: 'asia-pacific',
  Lebanon: 'asia-pacific',
  Palestine: 'asia-pacific',
  Jordan: 'asia-pacific',

  // --- Africa ------------------------------------------------------
  Nigeria: 'africa',
  Senegal: 'africa',
  Cameroon: 'africa',
  Angola: 'africa',
  Guinea: 'africa',
  'Ivory Coast': 'africa',
  'South Sudan': 'africa',
  'Cape Verde': 'africa',
  'Central African Republic': 'africa',
  Uganda: 'africa',
}

/**
 * Map a country name (as stored on `players.nationality`) to its
 * region slug. Returns 'other' for anything not explicitly mapped —
 * keeps the function total so callers don't need to handle nulls.
 *
 * Case-insensitive match: 'USA', 'usa', and 'Usa' all resolve. This
 * is a defense against the bulk import's mixed casing and against
 * future agent-typed entries.
 */
export function getRegionFor(country: string): RegionSlug {
  const direct = COUNTRY_TO_REGION[country]
  if (direct) return direct
  // Case-insensitive fallback. Walk the map once — n=68, negligible.
  const lower = country.toLowerCase()
  for (const [name, slug] of Object.entries(COUNTRY_TO_REGION)) {
    if (name.toLowerCase() === lower) return slug
  }
  return 'other'
}

export function getRegionMeta(slug: string): RegionMeta | null {
  return REGIONS.find((r) => r.slug === slug) ?? null
}
